import { substitutePathParams } from '@/services/router/generate-path';
import { envChannel } from './channels';
import { toRealtimeEvent, type RealtimeEvent, type RealtimeTransport } from './events';
import { createMemberTracker } from './member-tracker';
import { MAX_LOCATION_PATH_BYTES } from './message';
import { createPeerStore, type PeerStore } from './peer-store';

/** This tab's hold on the workspace channel: who else is in the workspace and where they are. */
export type WorkspacePresence = {
  /** Every other tab in the workspace, including the user's own other tabs, with its last `location`. */
  store: PeerStore;
  /** Tells the others where this tab is. Paths the server would refuse (not in this workspace, too long) are not sent. */
  setLocation(path: string): void;
  /** Leaves the channel. */
  close(): void;
};

const byteLength = (text: string) => new TextEncoder().encode(text).length;

/**
 * Joins `env:<id>` for the tab. A tab's location is never stored by the server, so a tab sends it
 * when it changes and again to each tab that joins, which is how late joiners catch up. A peer
 * that crashes is dropped by the server's presence expiry (a leave), not by a timeout here.
 */
export function createWorkspacePresence(transport: RealtimeTransport, environmentId: number): WorkspacePresence {
  const channel = envChannel(environmentId);
  const prefix = substitutePathParams('/workspaces/:workspaceId/', { workspaceId: environmentId });
  const store = createPeerStore();
  let path: string | null = null;

  const publishLocation = () => {
    if (path !== null) transport.publish(channel, { type: 'location', data: { path } });
  };

  const members = createMemberTracker(transport, channel, store);

  const onEvent = (event: RealtimeEvent) => {
    if (event.kind === 'subscribed') {
      void members.refresh();
      // After a reconnect the others' view of us may be gone.
      publishLocation();
    } else if (event.kind === 'message') {
      if (event.from !== null && event.message.type === 'location') members.heard(event.from, event.message);
    } else if (event.kind === 'leave') {
      members.leave(event.member);
    } else if (members.join(event.member)) {
      // Whoever just arrived has missed what we said before; tell them where we are.
      publishLocation();
    }
  };

  const unsubscribe = transport.subscribe(channel, (raw) => {
    const event = toRealtimeEvent(raw);
    if (event !== null) onEvent(event);
  });

  void members.refresh();

  return {
    store,
    setLocation(next) {
      if (!next.startsWith(prefix) || byteLength(next) > MAX_LOCATION_PATH_BYTES) return;
      path = next;
      publishLocation();
    },
    close: unsubscribe,
  };
}
